import * as React from 'react';

import { useLockFn, useRequest, useUpdateEffect } from 'ahooks';
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  Empty,
  Flex,
  Modal,
  notification,
  Skeleton,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import {
  BellOutlinedIcon,
  LogoutOutlinedIcon,
  MenuOutlinedIcon,
  UserOutlinedIcon,
} from 'components';
import { getUser, signOut } from 'services';
import { generateDownloadFile, PATHNAME, STORAGE } from 'utils';

const {
  LOCAL: {
    LOGIN: { BASE_URL: LOGIN_BASE_URL },
  },
} = PATHNAME;
const {
  LOCAL: {
    DEFAULT: { VERSION: DEFAULT_LOCAL_VERSION },
  },
} = STORAGE;

type PrivateHeaderProps = {
  userId?: string;
  toggleSidebar: () => void;
};

const PrivateHeader = ({ userId, toggleSidebar }: PrivateHeaderProps) => {
  const [signOutModalInstance, signOutModalContextHolder] = Modal.useModal();
  const {
    data: userData,
    loading: userLoading,
    run: runUser,
  } = useRequest(getUser, {
    manual: true,
  });
  const navigate = useNavigate();

  const { displayName, firstName, lastName, roleName, avatarId, phoneNumber } =
    userData ?? {};

  useUpdateEffect(() => {
    if (userId) {
      runUser({ id: userId });
    }
  }, [userId]);

  const handleSignOut = useLockFn(async () => {
    signOut()
      .then(() => {
        navigate(LOGIN_BASE_URL);
      })
      .catch(message => {
        notification.error({ message });
      });
  });

  return (
    <Flex wrap justify='space-between' align='center'>
      <Flex align='center' gap='small'>
        <div>
          <Button
            type='text'
            size='large'
            icon={<MenuOutlinedIcon />}
            onClick={toggleSidebar}
          />
          <span className='sr-only'>آیکون منو</span>
        </div>
        <Badge
          count={`نسخه ${DEFAULT_LOCAL_VERSION}`}
          color='var(--ant-color-primary)'
          className='mb-1'
        />
      </Flex>
      <Flex align='center' gap='middle'>
        <Dropdown
          trigger={['click']}
          overlayClassName='pt-1 max-w-xl'
          menu={{
            items: false
              ? [
                  {
                    key: 'notification-success',
                    label: (
                      <Typography.Text type='success'>
                        پیغام موفقیت: لورم ایپسوم متن ساختگی با تولید سادگی
                        نامفهوم از صنعت چاپ و با استفاده از طراحان گرافیک است.
                        چاپگرها و متون بلکه روزنامه و مجله در ستون و سطرآنچنان
                        که لازم است و برای شرایط فعلی تکنولوژی مورد نیاز و
                        کاربردهای متنوع با هدف بهبود ابزارهای کاربردی می باشد.
                      </Typography.Text>
                    ),
                  },
                  {
                    type: 'divider',
                  },
                  {
                    key: 'notification-warning',
                    label: (
                      <Typography.Text type='warning'>
                        پیغام هشدار: لورم ایپسوم متن ساختگی با تولید سادگی
                        نامفهوم از صنعت چاپ و با استفاده از طراحان گرافیک است.
                        چاپگرها و متون بلکه روزنامه و مجله در ستون و سطرآنچنان
                        که لازم است و برای شرایط فعلی تکنولوژی مورد نیاز و
                        کاربردهای متنوع با هدف بهبود ابزارهای کاربردی می باشد.
                      </Typography.Text>
                    ),
                  },
                  {
                    type: 'divider',
                  },
                  {
                    key: 'notification-secondary',
                    label: (
                      <Typography.Text type='secondary'>
                        پیغام خاکستری: لورم ایپسوم متن ساختگی با تولید سادگی
                        نامفهوم از صنعت چاپ و با استفاده از طراحان گرافیک است.
                        چاپگرها و متون بلکه روزنامه و مجله در ستون و سطرآنچنان
                        که لازم است و برای شرایط فعلی تکنولوژی مورد نیاز و
                        کاربردهای متنوع با هدف بهبود ابزارهای کاربردی می باشد.
                      </Typography.Text>
                    ),
                  },
                  {
                    type: 'divider',
                  },
                  {
                    key: 'notification-danger',
                    label: (
                      <Typography.Text type='danger'>
                        پیغام خطر: لورم ایپسوم متن ساختگی با تولید سادگی نامفهوم
                        از صنعت چاپ و با استفاده از طراحان گرافیک است. چاپگرها و
                        متون بلکه روزنامه و مجله در ستون و سطرآنچنان که لازم است
                        و برای شرایط فعلی تکنولوژی مورد نیاز و کاربردهای متنوع
                        با هدف بهبود ابزارهای کاربردی می باشد.
                      </Typography.Text>
                    ),
                  },
                ]
              : [
                  {
                    key: 'notification-empty',
                    label: (
                      <Empty
                        className='mx-20 my-10'
                        description='هیچ پیغامی برای نمایش وجود ندارد.'
                        imageStyle={{
                          height: '5rem',
                        }}
                      />
                    ),
                    disabled: true,
                  },
                ],
          }}
        >
          <Button
            shape='circle'
            color='default'
            variant='filled'
            icon={<BellOutlinedIcon className='text-lg' />}
          />
        </Dropdown>
        <Dropdown
          arrow
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'user-info',
                icon: <UserOutlinedIcon />,
                label: userLoading ? (
                  <Skeleton.Node active className='h-4 w-24 align-baseline' />
                ) : (
                  <span>
                    {displayName ||
                      (firstName
                        ? firstName.concat(` ${lastName || ''}`)
                        : phoneNumber)}
                  </span>
                ),
                extra: userLoading ? (
                  <Skeleton.Button
                    active
                    size='small'
                    shape='round'
                    className='h-5'
                  />
                ) : (
                  <Badge count={roleName} color='blue' />
                ),
              },
              {
                type: 'divider',
              },
              {
                key: 'user-sign-out',
                icon: <LogoutOutlinedIcon />,
                label: (
                  <React.Fragment>
                    <button
                      type='button'
                      onClick={async () => {
                        const confirmed = await signOutModalInstance.confirm({
                          title: `آیا از ترک حساب خود اطمینان دارید؟`,
                          centered: true,
                          width: 480,
                        });
                        if (confirmed) {
                          handleSignOut();
                        }
                      }}
                    >
                      خروج از حساب
                    </button>
                    <React.Fragment>{signOutModalContextHolder}</React.Fragment>
                  </React.Fragment>
                ),
                danger: true,
              },
            ],
          }}
        >
          <Avatar
            src={avatarId ? generateDownloadFile('User', avatarId) : null}
            icon={<UserOutlinedIcon />}
            className='cursor-pointer'
          />
        </Dropdown>
      </Flex>
    </Flex>
  );
};

export { PrivateHeader };
